import dayjs, { Dayjs } from 'dayjs'
import React, { useCallback, useMemo } from 'react'
import { FieldHookConfig } from 'formik'
import { useField } from '@/hooks/useField'

import ErrorMessage from '@/components/formik/messages/ErrorMessage'
import TimePickerInput, { TimePickerInputProps } from '@/components/ui/inputs/TimePickerInput'

type TimePickerFieldProps = Omit<TimePickerInputProps, 'onChange' | 'value'> &
  FieldHookConfig<string> & {
    disableHelperText?: boolean
    readOnly?: boolean
  }

const MemoizedTimePickerInput = React.memo(TimePickerInput)

export default function TimePickerField(props: TimePickerFieldProps) {
  const { disabled, disableHelperText = false, name, readOnly, ...rest } = props
  const [field, meta, helpers] = useField<string>(name)

  const value = useMemo(() => (meta.value === '' ? null : dayjs(meta.value, 'HH:mm', true)), [meta.value])

  const handleChange = useCallback(
    (newValue: Dayjs | null, inputValue?: string) => {
      helpers.setValue(newValue === null ? '' : newValue?.isValid() ? newValue.format('HH:mm') : inputValue || '')
    },
    [helpers]
  )

  return (
    <MemoizedTimePickerInput
      {...rest}
      disabled={disabled}
      error={!!meta.error && meta.touched && !disabled && !readOnly}
      helperText={!disableHelperText && !!meta.error && meta.touched ? <ErrorMessage error={meta.error} /> : undefined}
      name={field.name}
      onBlur={field.onBlur}
      onChange={handleChange}
      readOnly={readOnly}
      value={value}
    />
  )
}
